import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Heart, Trash2, ArrowLeft, Sparkles } from "lucide-react";

const WD = ["D", "S", "T", "Q", "Q", "S", "S"];

export default function FPCarePlan() {
  const [params] = useSearchParams();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [bid, setBid] = useState(params.get("beneficiary_id") || "");
  const [items, setItems] = useState([]);
  const [frequencies, setFrequencies] = useState({});
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ activity: "", frequency: "monthly", weekdays: [], last_at: "", responsible: "", notes: "" });

  useEffect(() => {
    api.get("/fp/beneficiaries").then((r) => {
      setBeneficiaries(r.data);
      if (!bid && r.data[0]) setBid(r.data[0].id);
    });
    api.get("/fp/care-plan/frequencies").then((r) => setFrequencies(r.data));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!bid) return;
    setLoading(true);
    api.get(`/fp/care-plan/${bid}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [bid]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!form.activity) return toast.error("Informe a atividade");
    try {
      const { data } = await api.post(`/fp/care-plan/${bid}/items`, form);
      setItems((it) => [data, ...it]);
      setForm({ activity: "", frequency: "monthly", weekdays: [], last_at: "", responsible: "", notes: "" });
      setAdding(false);
      toast.success("Rotina adicionada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remover esta rotina?")) return;
    try {
      await api.delete(`/fp/care-plan/items/${id}`);
      setItems((it) => it.filter((x) => x.id !== id));
      toast.success("Rotina removida");
    } catch { toast.error("Erro"); }
  };

  const toggleWD = (d) => setForm((f) => ({
    ...f,
    weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d],
  }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-care-plan">
      <Link to="/filho-presente/painel" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62] mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-5 h-5 text-amber-500" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62]">Plano de cuidados</h1>
      </div>
      <p className="text-slate-600 text-sm mb-6">Organize a rotina recorrente de acompanhamentos — sem virar prontuário médico.</p>

      {beneficiaries.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-10 text-center">
          <div className="text-slate-700 mb-3">Cadastre um familiar primeiro.</div>
          <Link to="/filho-presente/familiares/novo" className="bg-[#0B2B62] text-white px-4 py-2 rounded-lg text-sm">Cadastrar familiar</Link>
        </div>
      ) : (
        <>
          {beneficiaries.length > 1 && (
            <div className="mb-5">
              <label className="text-sm text-slate-600">Beneficiário:</label>
              <select value={bid} onChange={(e) => setBid(e.target.value)} data-testid="fp-cp-benef"
                className="ml-2 px-3 py-1.5 border border-slate-300 rounded-md text-sm">
                {beneficiaries.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl">
            {loading ? <div className="p-6 text-slate-500">Carregando…</div>
              : items.length === 0 ? (
                <div className="p-8 text-center">
                  <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <div className="text-slate-700 font-medium mb-1">Nenhuma rotina cadastrada</div>
                  <div className="text-slate-500 text-sm mb-4">Adicione consultas e terapias recorrentes.</div>
                </div>
              ) : (
                <div className="divide-y">
                  {items.map((it) => (
                    <div key={it.id} className="p-4 flex items-start gap-3">
                      <div className="w-1 self-stretch bg-amber-400 rounded" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800">{it.activity}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          <span className="text-amber-700 font-medium">{it.frequency_label}</span>
                          {it.next_at && <> · próximo: <span className="font-medium">{new Date(it.next_at + "T00:00").toLocaleDateString("pt-BR")}</span></>}
                          {it.responsible && <> · com {it.responsible}</>}
                        </div>
                        {it.notes && <div className="text-xs text-slate-500 mt-1">{it.notes}</div>}
                      </div>
                      <button onClick={() => removeItem(it.id)} data-testid={`fp-cp-remove-${it.id}`}
                        className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}

            <div className="border-t p-4">
              {!adding ? (
                <button onClick={() => setAdding(true)} data-testid="fp-cp-add-btn"
                  className="inline-flex items-center gap-1.5 text-sm text-[#0B2B62] font-medium hover:underline">
                  <Plus className="w-4 h-4" /> Adicionar rotina
                </button>
              ) : (
                <form onSubmit={addItem} className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} required
                      data-testid="fp-cp-activity" placeholder="Ex.: Cardiologista, Fisioterapia..."
                      className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
                    <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                      data-testid="fp-cp-frequency" className="px-3 py-2 border border-slate-300 rounded-md text-sm">
                      {Object.entries(frequencies).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  {(form.frequency === "weekly" || form.frequency === "twice_a_week") && (
                    <div>
                      <div className="text-xs text-slate-600 mb-1">Dias da semana:</div>
                      <div className="flex gap-1">
                        {WD.map((d, i) => (
                          <button key={i} type="button" onClick={() => toggleWD(i)}
                            className={`w-8 h-8 rounded-full text-xs font-medium border ${
                              form.weekdays.includes(i) ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white border-slate-300 text-slate-500"
                            }`}>{d}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input type="date" value={form.last_at} onChange={(e) => setForm({ ...form, last_at: e.target.value })}
                      placeholder="Última realização" data-testid="fp-cp-last"
                      className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
                    <input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })}
                      placeholder="Responsável (opcional)" className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
                  </div>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                    placeholder="Observações (opcional)"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setAdding(false)} className="px-3 py-1.5 text-sm text-slate-500">Cancelar</button>
                    <button type="submit" data-testid="fp-cp-save"
                      className="px-4 py-1.5 bg-[#0B2B62] text-white rounded-md text-sm">Adicionar</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
